// External libraries
import { RetryLink } from 'apollo-link-retry';
import { strictIsFinite } from '@checkout/react-components';

// Internal modules
import { $logger } from '@/lib/logger';
import { camelToSnakeCase } from '@/lib/utils';
import { getApiCorrelationId } from '../utils';

/**
 * Mutations and queries in this set are not allowed to be retried
 *
 * SignUpNewMember:
 *  Retries on this mutation can cause ACCOUNT_ALREADY_EXISTS
 *  errors if the OAS call already succeeded or if it is in-flight
 *  and the error is due to an OAS socket timeout.
 */
const RETRY_OPERATION_BLACKLIST = new Set(['SignUpNewMemberMutation']);

const MAX_ATTEMPTS = 3;

const isRetryBlacklisted = ({ operationName }) => {
  return RETRY_OPERATION_BLACKLIST.has(operationName);
};

export const retryLink = new RetryLink({
  attempts: (count, operation, error) => {
    const operationName = camelToSnakeCase(operation.operationName);
    const isBlacklisted = isRetryBlacklisted(operation);
    const { skipRetries = false, maxAttempts } = operation.getContext();
    const { statusCode } = error;

    // Certain operations are blacklisted from being retried
    if (isRetryBlacklisted || skipRetries || statusCode < 500) {
      $logger.info(`${operationName}_skip_retry`, {
        isBlacklisted,
        skipRetries,
        statusCode,
      });
      return false;
    }

    const max = strictIsFinite(maxAttempts) ? maxAttempts : MAX_ATTEMPTS;
    $logger.info(`retry_${operationName}_after_attempt_${count}_failed`, {
      apiCorrelationId: getApiCorrelationId({ networkError: error }),
      attemptsRemaining: max - count,
      error: error.message,
    });

    return count < max;
  },
});
